import { Download, File, Loader2, MessageSquare } from "lucide-react";
import { useState } from "react";
import {
  useGetSalesUnitByIngredient,
  useGetSupportingDocumentDownloadUrl
} from "../../../../../api/ingredients";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../../components/ui/common/card";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import { downloadFileFromUrl } from "../../../../../utils/utils";
import { formatDate } from "../../../../ingredients/shared/components/ImpactResultDocuments/utils/documentUtils";
import DocumentNotesModal from "./DocumentNotesModal";

interface SupportingDocumentApi {
  fileName?: string;
  key?: string;
  // Backend field is `notes` (see `supporting_document.schema.ts`) - the
  // separate edit-mode upload form saves it under a `note`/
  // `supportingDocuments_notes` field instead, so both are read here in case
  // that ever gets reconciled.
  notes?: string;
  note?: string;
  uploadedBy?: string;
  uploadedAt?: string;
}

interface ImpactResultDocumentsReadOnlyProps {
  ingredientId: string;
  supplierId: string;
}

type PendingAction = "view" | "download" | null;

/**
 * Read-only "Impact result documents" card for the Ingredient Details
 * ("view ingredient") modal only - a Tailwind rebuild that replaces the
 * shared, edit-capable `ImpactResultDocuments` component (which still backs
 * the register/edit forms) with a simple list of already-uploaded documents
 * and view/download actions. Sits in `SummaryTab.tsx` next to `LcaDataSection`
 * (the same place the shared component renders in edit mode), fetching its
 * own data via the same `useGetSalesUnitByIngredient` call.
 */
const ImpactResultDocumentsReadOnly = ({
  ingredientId,
  supplierId
}: ImpactResultDocumentsReadOnlyProps) => {
  const [notesDoc, setNotesDoc] = useState<SupportingDocumentApi | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const { data: ingredientDetailData, isFetching } = useGetSalesUnitByIngredient({
    ingredientId,
    supplierId
  });

  const { mutate: resolveDownloadUrl } = useGetSupportingDocumentDownloadUrl();

  if (!ingredientId || !supplierId) return null;

  const documents: SupportingDocumentApi[] = Array.isArray(
    ingredientDetailData?.supportingDocuments
  )
    ? ingredientDetailData.supportingDocuments
    : [];

  const resolveAndUse = (
    doc: SupportingDocumentApi,
    action: PendingAction,
    onSignedUrl: (signedUrl: string) => void
  ) => {
    if (!doc.key || pendingKey) return;

    console.log(`Resolving ${action} URL for document ${doc.key}...`);

    setPendingKey(doc.key);
    setPendingAction(action);
    resolveDownloadUrl(doc.key, {
      onSuccess: ({ signedUrl }) => onSignedUrl(signedUrl),
      onSettled: () => {
        setPendingKey(null);
        setPendingAction(null);
      }
    });
  };

  // const handleViewDocument = (doc: SupportingDocumentApi) =>
  //   resolveAndUse(doc, "view", signedUrl =>
  //     window.open(signedUrl, "_blank", "noopener,noreferrer")
  //   );

  const handleDownloadDocument = (doc: SupportingDocumentApi) =>
    resolveAndUse(doc, "download", signedUrl =>
      downloadFileFromUrl(signedUrl, doc.fileName || "document", false)
    );

  return (
    <>
      <Card className="tw-gap-3 tw-py-4">
        <CardHeader className="tw-px-4">
          <h3 className="tw-text-base tw-font-semibold tw-text-foreground">
            Impact result documents
          </h3>
          <p className="tw-text-sm tw-text-muted-foreground">
            Reports, calculation sheets and supplier evidence behind these results.
          </p>
        </CardHeader>
        <CardContent className="tw-px-4">
          {isFetching ? (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Skeleton className="tw-h-10 tw-w-full" />
              <Skeleton className="tw-h-10 tw-w-full" />
            </div>
          ) : documents.length === 0 ? (
            <div className="tw-border-t tw-border-border tw-pt-4 tw-py-6 tw-text-center">
              <p className="tw-text-sm tw-text-muted-foreground">No documents uploaded yet.</p>
            </div>
          ) : (
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-border tw-pb-2">
                <span className="tw-text-sm tw-font-medium tw-text-muted-foreground">
                  Document Name
                </span>
                <span className="tw-text-sm tw-font-medium tw-text-muted-foreground">Actions</span>
              </div>

              <div className="tw-divide-y tw-divide-border">
                {documents.map(doc => {
                  const isResolving = !!doc.key && pendingKey === doc.key;

                  return (
                    <div
                      key={doc.key}
                      className="tw-flex tw-flex-col tw-gap-3 tw-py-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                    >
                      <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                        <File className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-muted-foreground" />
                        <div className="tw-min-w-0">
                          <p className="tw-font-medium tw-text-foreground tw-truncate">
                            {doc.fileName}
                          </p>
                          {doc.uploadedAt && (
                            <p className="tw-text-xs tw-text-muted-foreground tw-mt-1">
                              {formatDate(new Date(doc.uploadedAt))}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setNotesDoc(doc)}
                        >
                          <MessageSquare className="tw-h-4 tw-w-4" />
                          View notes
                        </Button>
                        {/* <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isResolving}
                          onClick={() => handleViewDocument(doc)}
                        >
                          {isResolving && pendingAction === "view" ? (
                            <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
                          ) : (
                            <Eye className="tw-h-4 tw-w-4" />
                          )}
                          View document
                        </Button> */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isResolving}
                          onClick={() => handleDownloadDocument(doc)}
                        >
                          {isResolving && pendingAction === "download" ? (
                            <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
                          ) : (
                            <Download className="tw-h-4 tw-w-4" />
                          )}
                          Download
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DocumentNotesModal
        open={!!notesDoc}
        onOpenChange={open => !open && setNotesDoc(null)}
        fileName={notesDoc?.fileName}
        note={notesDoc?.notes ?? notesDoc?.note}
      />
    </>
  );
};

export default ImpactResultDocumentsReadOnly;
